<template>
    <div>
        <section class="w-full bg-blue-100">
            <div class="bg-white rounded-b-2xl">
                <div class="self-gutter">
                    <div class="self-gutter-y self-content-sm">
                        <ReportNavigation v-if="this.$route.path.startsWith(`/${$i18n.locale}/reports`)" />
                        <h1 class="title-2xl">{{ $t('passenger_report.title') }}</h1>
                        <AlertSummary
                            v-if="!generateReportOk"
                            :main-text="$t('error.main_text')"
                            :secondary-text="$t('error.secondary_text')"
                            alert-classes="mt-24 e5-form-error-summary--error"
                        />

                        <div class="pt-24 self-hero-intro expandable-description" :class="{ 'show-full-desc': showFullDesc }">
                            <p v-html="$t('passenger_report.desc')"></p>
                            <a ref="showFullDesc" class="text-blue-400 cursor-pointer expand-link bg-white" style="font-style: italic">{{
                                $t('reports.read_more')
                            }}</a>
                        </div>
                        <!--Passenger report form-->
                        <div class="pt-24 self-form-wrap">
                            <div class="w-full sm:w-5/12 self-form-wrap-item" :class="{ 'e5-error': !generateReportOk }">
                                <label class="self-form-item-label" for="calendar"
                                    >{{ $t('reports.form.period') }}:<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <button
                                        ref="calendarButton"
                                        id="calendar"
                                        class="e5-input-calendar"
                                        :class="{ 'e5-error': !generateReportOk }"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        @click="dropdown('calendarButton')"
                                    >
                                        <span :class="{ 'text-alert': !generateReportOk }">
                                            {{
                                                ServiceByDateRequest.service.servicestartdate || ServiceByDateRequest.service.serviceenddate
                                                    ? selectedDateRange
                                                    : $t('reports.form.period_placeholder')
                                            }}</span
                                        >
                                    </button>

                                    <div class="e5-input-icon">
                                        <CalendarSVG />
                                    </div>
                                    <DropDownCalendar v-model="selectedDateRange" @input="handleDateChange" />
                                </div>
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentMonth')">{{
                                    $t('reports.form.current_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastMonth')">{{
                                    $t('reports.form.last_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentYear')">{{
                                    $t('reports.form.current_year')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastYear')">{{
                                    $t('reports.form.last_year')
                                }}</a>
                            </div>

                            <div class="w-full xs:w-1/2 sm:w-3/12 self-form-wrap-item">
                                <label class="self-form-item-label" for="search-by">{{ $t('reports.form.search_by') }}:</label>
                                <div class="relative">
                                    <div class="self-form-select-wrap">
                                        <select class="e5-select" name="search-by" id="search-by" v-model="ServiceByDateRequest.datetype">
                                            <option value="SERVICE_DATE">{{ $t('reports.form.search_by_option1') }}</option>
                                            <option value="INVOICE_DATE">{{ $t('reports.form.search_by_option2') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="w-full xs:w-1/2 sm:w-4/12 self-form-wrap-item">
                                <label class="self-form-item-label">{{ $t('reports.form.sales_channels') }}:</label>

                                <div class="flex">
                                    <div class="block mr-24">
                                        <input
                                            class="e5-checkbox"
                                            checked
                                            type="checkbox"
                                            id="online"
                                            name="online"
                                            value="ONLINE"
                                            v-model="selectedSalesChannels"
                                        />
                                        <label class="" for="online">
                                            {{ $t('reports.form.online') }}
                                        </label>
                                    </div>

                                    <div class="block">
                                        <input
                                            class="e5-checkbox"
                                            checked
                                            type="checkbox"
                                            id="offline"
                                            name="offline"
                                            value="OFFLINE"
                                            v-model="selectedSalesChannels"
                                        />
                                        <label class="" for="offline">
                                            {{ $t('reports.form.offline') }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="passenger_name">{{ $t('reports.form.passenger_name') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="passenger_name"
                                        placeholder=""
                                        name="passenger_name"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.passenger.name"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="reference">{{ $t('reports.form.passenger_ref_no') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="reference"
                                        placeholder=""
                                        name="reference"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.passenger.reference"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="booked">{{ $t('reports.form.booker_name') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="booked"
                                        placeholder=""
                                        name="booked"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.booking.bookedbyname"
                                    />
                                </div>
                            </div>
                            <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                <label class="self-form-item-label" for="customer">{{ $t('reports.form.customer_ref') }}:</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="customer"
                                        placeholder=""
                                        name="customer"
                                        class="self-form-input"
                                        v-model="ServiceByDateRequest.booking.customerreference"
                                    />
                                </div>
                            </div>
                            <div class="w-full self-form-wrap-item">
                                <div class="flex justify-between">
                                    <label class="self-form-item-label">{{ $t('reports.form.service_groups') }}:</label>
                                    <button v-if="!this.SelectedToplevels.length" class="self-btn-plain" @click="selectAllToplevels">
                                        <span class="self-btn-plain__text">{{ $t('reports.form.select_all') }}</span>
                                    </button>
                                    <button v-else class="self-btn-plain" @click="clearServiceGroups">
                                        <span class="self-btn-plain__text">{{ $t('reports.form.clear_all') }}</span>
                                    </button>
                                </div>
                                <ul class="self-checkboxes-list">
                                    <!--eslint-disable-next-line-->
                                    <li v-for="(item, index) in toplevel" v-bind:key="index" v-if="index % 2 === 0">
                                        <div class="block">
                                            <input
                                                type="checkbox"
                                                :id="item.toplevel_id"
                                                :value="{ toplevel_id: item.toplevel_id, toplevel_name: item.toplevel_name }"
                                                class="e5-checkbox"
                                                checked
                                                v-model="SelectedToplevels"
                                            />
                                            <label :for="item.toplevel_id">{{ item.toplevel_name }}</label>
                                        </div>
                                        <!-- Check if there's a next item and display it -->
                                        <div class="block" v-if="toplevel[index + 1]">
                                            <input
                                                type="checkbox"
                                                :id="toplevel[index + 1].toplevel_id"
                                                :value="{
                                                    toplevel_id: toplevel[index + 1].toplevel_id,
                                                    toplevel_name: toplevel[index + 1].toplevel_name
                                                }"
                                                class="e5-checkbox"
                                                checked
                                                v-model="SelectedToplevels"
                                            />
                                            <label :for="toplevel[index + 1].toplevel_id">{{ toplevel[index + 1].toplevel_name }}</label>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div aria-labelledby="hiddenrow" class="flex flex-wrap w-full max-h-0 overflow-hidden">
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="code">{{ $t('reports.form.project_code') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="code"
                                            placeholder=""
                                            name="code"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.projectcode"
                                        />
                                    </div>
                                </div>
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="budget">{{ $t('reports.form.budget_code') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="budget"
                                            placeholder=""
                                            name="budget"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.budgetcode"
                                        />
                                    </div>
                                </div>

                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="account">{{ $t('reports.form.fin_account') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="account"
                                            placeholder=""
                                            name="account"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.finaccount"
                                        />
                                    </div>
                                </div>
                                <div class="w-1/2 sm:w-1/4 self-form-wrap-item">
                                    <label class="self-form-item-label" for="me">{{ $t('reports.form.me_number') }}:</label>
                                    <div class="relative">
                                        <input
                                            type="text"
                                            id="me"
                                            placeholder=""
                                            name="me"
                                            class="self-form-input"
                                            v-model="ServiceByDateRequest.booking.menumber"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="self-form-wrap-actions">
                                <div>
                                    <button
                                        v-if="$store.state.invoiceExtraFields"
                                        ref="animateToggleButton"
                                        aria-expanded="false"
                                        id="hiddenrow"
                                        class="self-btn-plain"
                                        @click="animatetoggle('animateToggleButton')"
                                        data-toggle="animatetoggle"
                                    >
                                        <span class="self-btn-plain__text">{{ $t('reports.form.more_options') }}</span>
                                        <span class="self-btn-plain__icon self-moreinfo-icon"><IconArrowForwardSVG /></span>
                                    </button>
                                </div>

                                <div class="flex xs-down:justify-end xs-down:pt-24">
                                    <a class="mr-24 self-btn-secondary" @click="clearAll">{{ $t('reports.form.clear_all') }}</a>

                                    <button class="self-btn-primary" :disabled="loading" @click="generateOnClick(ServiceByDateRequest)">
                                        {{ $t('reports.form.generate_report') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- TABLIST FOR REPORT TABLE -->
                        <div v-if="serviceByDateResponse && requestSent" class="pt-24 -mb-24 md:-mb-48">
                            <ul class="self-table-tab-controls" role="tablist">
                                <li role="presentation">
                                    <a class="self-table-tab-control-item" href="#summary" role="tab" id="summary" aria-selected="true">{{
                                        $t('reports.table.summary')
                                    }}</a>
                                </li>
                                <li role="presentation">
                                    <a class="self-table-tab-control-item" href="#detailed" role="tab" id="detailed" tabindex="-1">{{
                                        $t('reports.table.detailed')
                                    }}</a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Service report table -->
        <article v-if="serviceByDateResponse && requestSent">
            <div id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content">
                        <!-- Summary view -->
                        <div
                            class="w-full"
                            id="summary"
                            role="tabpanel"
                            aria-labelledby="summary"
                            aria-hidden="false"
                            tabindex="-1"
                            v-if="serviceByDateResponse && requestSent"
                        >
                            <div class="self-table-info">
                                <div class="flex items-center sm-down:w-full sm-down:justify-between">
                                    <span class="block text-base"
                                        >{{ $t('reports.table.total') }}:
                                        <span class="font-semibold whitespace-nowrap">{{
                                            formatSum(totalSum(serviceByDateResponse))
                                        }}</span></span
                                    >

                                    <div class="self-table-info-entries-count"></div>
                                </div>

                                <div class="flex items-center sm-down:w-full sm-down:justify-between sm-down:pt-16">
                                    <div class="self-table-info-entries-select"></div>

                                    <div class="relative inline-block">
                                        <div class="relative inline-block">
                                            <button
                                                ref="exportButton"
                                                class="self-btn-plain"
                                                id="export-report"
                                                aria-haspopup="true"
                                                aria-expanded="false"
                                                @click="dropdown('exportButton')"
                                            >
                                                <span class="self-btn-plain__text">{{ $t('reports.table.export') }}</span>
                                                <span class="self-btn-plain__icon self-moreinfo-icon">
                                                    <IconArrowForwardSVG />
                                                </span>
                                            </button>

                                            <div class="e5-dropdown e5-dropdown-xs" aria-labelledby="export-report">
                                                <div class="p-10">
                                                    <a
                                                        class="py-5 self-text-link hover:cursor-pointer"
                                                        @click="
                                                            download(
                                                                serviceByDateResponse,
                                                                '.xlsx',
                                                                'summary-passenger',
                                                                serviceByDateResponseSummary
                                                            )
                                                        "
                                                        >Excel (XLSX)</a
                                                    >
                                                    <a
                                                        class="py-5 self-text-link hover:cursor-pointer"
                                                        @click="
                                                            download(
                                                                serviceByDateResponse,
                                                                '.csv',
                                                                'summary-passenger',
                                                                serviceByDateResponseSummary
                                                            )
                                                        "
                                                        >CSV</a
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <table class="w-full self-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <span>{{ $t('reports.table.passenger_name') }}</span>
                                        </th>
                                        <th class="self-table-price sum">
                                            <span>{{ $t('reports.table.invoice_sum') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in serviceByDateResponseSummary.entries()" :key="item[0]" class="summary-row">
                                        <td>{{ item[0] }}</td>
                                        <td class="self-table-price">{{ formatSum(item[1]) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!--Detailed view-->
                        <div
                            class="w-full"
                            id="detailed"
                            role="tabpanel"
                            aria-labelledby="detailed"
                            aria-hidden="true"
                            tabindex="-1"
                            hidden
                            v-if="serviceByDateResponse && requestSent"
                        >
                            <div class="self-table-info">
                                <div class="flex items-center sm-down:w-full sm-down:justify-between">
                                    <span class="block text-base"
                                        >{{ $t('reports.table.total') }}:
                                        <span class="font-semibold whitespace-nowrap">{{
                                            formatSum(totalSum(serviceByDateResponse))
                                        }}</span></span
                                    >

                                    <div class="self-table-info-entries-count"></div>
                                </div>

                                <div class="flex items-center sm-down:w-full sm-down:justify-between sm-down:pt-16">
                                    <div class="self-table-info-entries-select"></div>

                                    <div class="relative inline-block">
                                        <div class="relative inline-block">
                                            <button
                                                ref="exportButton2"
                                                class="self-btn-plain"
                                                id="export-report2"
                                                aria-haspopup="true"
                                                aria-expanded="false"
                                                @click="dropdown('exportButton2')"
                                            >
                                                <span class="self-btn-plain__text"> {{ $t('reports.table.export') }} </span>
                                                <span class="self-btn-plain__icon self-moreinfo-icon"><IconArrowForwardSVG /></span>
                                            </button>

                                            <div class="e5-dropdown e5-dropdown-xs" aria-labelledby="export-report2">
                                                <div class="p-10">
                                                    <a
                                                        class="py-5 self-text-link hover:cursor-pointer"
                                                        @click="download(serviceByDateResponse, '.xlsx', 'detailed-passenger')"
                                                        >Excel (XLSX)</a
                                                    >
                                                    <a
                                                        class="py-5 self-text-link hover:cursor-pointer"
                                                        @click="download(serviceByDateResponse, '.csv', 'detailed-passenger')"
                                                        >CSV</a
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <table class="w-full self-table self-table--compact">
                                <thead>
                                    <tr>
                                        <th>
                                            <span>
                                                <select id="passengerSelect" class="self-table-header-select">
                                                    <option value="">{{ $t('reports.table.passenger_name') }}</option>
                                                    <option v-for="passenger in uniquePassengers" :key="passenger" :value="passenger">
                                                        {{ passenger }}
                                                    </option>
                                                </select>
                                            </span>
                                        </th>
                                        <th>
                                            <span>{{ $t('reports.table.passenger_ref_no') }}</span>
                                        </th>
                                        <th>
                                            <span>{{ $t('reports.table.invoice_no') }}</span>
                                        </th>
                                        <th class="self-table-price sum">
                                            <span>{{ $t('reports.table.invoice_sum') }}</span>
                                        </th>
                                        <th>
                                            <span>
                                                <select id="bookedByNameSelect" class="self-table-header-select">
                                                    <option value="">{{ $t('reports.table.booked_by') }}</option>
                                                    <option v-for="name in uniqueBookedByNames" :key="name" :value="name">
                                                        {{ name }}
                                                    </option>
                                                </select>
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in this.serviceByDateResponse?.services ?? []" :key="item.service_id">
                                        <td v-if="item.servicepassengerlist.length > 0">
                                            <div v-for="passenger in item.servicepassengerlist" :key="passenger.passengerId">
                                                <p>{{ passenger.name }}</p>
                                            </div>
                                        </td>
                                        <td v-else></td>
                                        <td v-if="item.servicepassengerlist.length > 0">
                                            <div v-for="passenger in item.servicepassengerlist" :key="passenger.passengerId">
                                                <p>{{ passenger.reference }}</p>
                                            </div>
                                        </td>
                                        <td v-else></td>
                                        <td v-if="item.documents.length > 0 && findInvoice(item.documents) !== undefined">
                                            <a
                                                :href="findInvoice(item.documents).documentId"
                                                @click.prevent="
                                                    downloadInvoice(
                                                        findInvoice(item.documents).documentId,
                                                        findInvoice(item.documents).documentNo
                                                    )
                                                "
                                                >{{ findInvoice(item.documents).documentNo }}</a
                                            >
                                        </td>
                                        <td v-else></td>
                                        <td class="self-table-price">{{ formatSum(item.ttlsellrate) }}</td>
                                        <td>{{ item.bookedbyname }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </article>
        <!--Empty response-->
        <article v-if="!serviceByDateResponse">
            <section id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content-sm">
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-if="requestSent && responseReceived">{{
                            $t('reports.table.no_results')
                        }}</span>
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-else-if="requestSent">
                            <span class="loading-animation"
                                >{{ $t('reports.table.searching')
                                }}<span class="loading-dots">
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                </span></span
                            ></span
                        >
                    </div>
                </div>
            </section>
        </article>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { IToplevel } from '@/domain/IToplevel';
import CalendarSVG from '@/assets/static/icons-src/icon-calendar.svg';
import IconArrowForwardSVG from '@/assets/static/icons-src/icon-arrow-forward.svg';
import ArrowLeftSVG from '@/assets/static/icons-src/est-arrow-left.svg';
import LogoIllustrationSVG from '@/assets/static/icons-src/logo-illustration.svg';
import store from '@/store';
import { IReportServiceByDateResponce, IServiceByDateRequest } from '@/domain/IReport';
import { IPassenger } from '@/domain/IPassenger';
import { IBooking } from '@/domain/IBooking';
import { ITicket } from '@/domain/ITicket';
import { IService } from '@/domain/IService';
import $ from 'jquery';
import initTabs from '@/scripts/tabs';
import DropDownCalendar from '@/components/DropDownCalendar.vue';
import { IDocument } from '@/domain/IDocument';
import UtilsMixin from '@/scripts/utils';
import ReportNavigation from '@/components/ReportNavigation.vue';
import AlertSummary from '@/components/AlertSummary.vue';
import { eventBus } from '@/../eventBus';

@Component({
    components: {
        ReportNavigation,
        DropDownCalendar,
        CalendarSVG,
        IconArrowForwardSVG,
        ArrowLeftSVG,
        LogoIllustrationSVG,
        AlertSummary
    },
    metaInfo: {
        title: 'Passenger report'
    },
    mixins: [UtilsMixin]
})
export default class PassengerReport extends Vue {
    // Properties
    selectedDateRange = '';
    requestSent = false;
    private initExtraTable = true;
    generateReportOk = true;
    SelectedToplevels: IToplevel[] = [];
    selectedSalesChannels = ['ONLINE', 'OFFLINE'];
    private extraTable = null;
    responseReceived = false;
    loading = false;
    showFullDesc = false;
    ServiceByDateRequest: IServiceByDateRequest = {
        invoicedServices: '1',
        saleschannels: '',
        datetype: 'SERVICE_DATE',
        passenger: {
            passengerId: '',
            name: '',
            birthday: '',
            reference: ''
        } as unknown as IPassenger,
        booking: {
            bookingnumber: '',
            bookedbyname: '',
            customerreference: '',
            projectcode: '',
            budgetcode: '',
            employeenumber: '',
            menumber: '',
            finaccount: ''
        } as unknown as IBooking,
        toplevel: {
            toplevel_id: '',
            toplevel_name: ''
        } as unknown as IToplevel[],
        ticket: {
            ticketNo: ''
        } as unknown as ITicket,
        service: {
            id: '',
            servicestartdate: '',
            serviceenddate: '',
            bookingreferencenumber: '',
            servicedestinationcountry: '',
            servicedestinationcity: '',
            productname: ''
        } as unknown as IService,
        customerid: store.state.customerId
    };

    serviceByDateResponse: IReportServiceByDateResponce | null = null;

    // Computed properties

    get toplevel(): IToplevel[] {
        return store.state.toplevel;
    }

    get serviceByDateResponseSummary(): Map<string, number> | null {
        const responseSummary = new Map<string, number>();
        const response = this.serviceByDateResponse;
        if (response?.services?.length) {
            response.services.forEach((service) => {
                if (service.servicepassengerlist.length === 1) {
                    const passenger = service.servicepassengerlist[0].name;
                    const currentTotal = responseSummary.get(passenger) || 0;
                    responseSummary.set(passenger, currentTotal + service.ttlsellrate);
                } else if (service.servicepassengerlist.length > 1) {
                    const passengers = service.servicepassengerlist.map((passenger) => passenger.name).join(', ');
                    const currentTotal = responseSummary.get(passengers) || 0;
                    responseSummary.set(passengers, currentTotal + service.ttlsellrate);
                }
            });
        }
        return responseSummary;
    }

    get uniquePassengers(): string[] {
        return Array.from(
            new Set(
                (this.serviceByDateResponse?.services ?? []).flatMap((item) => item.servicepassengerlist.map((passenger) => passenger.name))
            )
        )
            .filter(Boolean)
            .sort();
    }

    get uniqueBookedByNames(): string[] {
        return Array.from(new Set((this.serviceByDateResponse?.services ?? []).map((item) => item.bookedbyname)))
            .filter(Boolean)
            .sort();
    }

    // Methods

    findInvoice(documents): IDocument | null {
        return documents.find((document) => document.documentType === 'INVOICE');
    }

    async generateOnClick(ServiceByDateRequest: IServiceByDateRequest) {
        if (this.selectedDateRange.split('—').length !== 2) {
            this.generateReportOk = false;
            this.scrollToAlertMessage();
        } else {
            this.scrollToResultsTable();
            this.ServiceByDateRequest.customerid = store.getters.getCustomerId;
            this.ServiceByDateRequest.toplevel =
                this.SelectedToplevels.length && this.SelectedToplevels.length !== 12 ? this.SelectedToplevels : [];
            this.ServiceByDateRequest.saleschannels = this.selectedSalesChannels.length === 1 ? this.selectedSalesChannels[0] : '';
            this.generateReportOk = true;
            this.requestSent = true;
            this.initExtraTable = true;
            this.serviceByDateResponse = null;
            this.responseReceived = false;
            this.loading = true;
            this.serviceByDateResponse = await store.dispatch('getServicesByDate', ServiceByDateRequest);
            this.loading = false;
            this.responseReceived = true;
        }
    }

    // Type Declarations for TypeScript to tell that these methods exist in utils.ts
    scrollToAlertMessage!: () => void;
    scrollToResultsTable!: () => void;
    initDescriptionExpansion!: () => void;

    clearAll() {
        this.ServiceByDateRequest = {
            invoicedServices: '1',
            saleschannels: '',
            datetype: 'SERVICE_DATE',
            passenger: {
                passengerId: '',
                name: '',
                birthday: '',
                reference: ''
            } as unknown as IPassenger,
            booking: {
                bookingnumber: '',
                bookedbyname: '',
                customerreference: '',
                projectcode: '',
                budgetcode: '',
                employeenumber: '',
                menumber: '',
                finaccount: ''
            } as unknown as IBooking,
            toplevel: {
                toplevel_id: '',
                toplevel_name: ''
            } as unknown as IToplevel[],
            ticket: {
                ticketNo: ''
            } as unknown as ITicket,
            service: {
                id: '',
                servicestartdate: '',
                serviceenddate: '',
                bookingreferencenumber: '',
                servicedestinationcountry: '',
                servicedestinationcity: '',
                productname: ''
            } as unknown as IService,
            customerid: store.state.customerId
        };
        this.SelectedToplevels = [];
        this.requestSent = false;
        this.$root.$emit('reset-calendar');
        this.selectedDateRange = '';
        this.destroyExtraTable();
        this.initExtraTable = true;
        this.selectedSalesChannels = ['ONLINE', 'OFFLINE'];
        this.responseReceived = false;
        this.serviceByDateResponse = null;
    }

    selectAllToplevels() {
        this.SelectedToplevels = this.toplevel;
    }

    clearServiceGroups() {
        this.SelectedToplevels = [];
    }

    private createExtraTable() {
        $.fn.dataTable.ext.errMode = 'throw';

        // Define a custom sorting plugin
        $.fn.dataTable.ext.type.order['formatted-num-asc'] = function (a, b) {
            a = parseFloat(a.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            b = parseFloat(b.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            return a - b;
        };

        $.fn.dataTable.ext.type.order['formatted-num-desc'] = function (a, b) {
            a = parseFloat(a.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            b = parseFloat(b.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            return b - a;
        };

        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedPassenger = $('#passengerSelect').val();
            if (settings.aoColumns.length < 1) {
                return true;
            }
            if (selectedPassenger === '') {
                return true;
            }
            const name = data[0].trim() || '';
            return name === selectedPassenger || name.includes(selectedPassenger);
        });
        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedBookedByName = $('#bookedByNameSelect').val() as string;
            if (settings.aoColumns.length < 5) {
                return true;
            }
            if (selectedBookedByName.trim() === '') {
                return true;
            }
            const name = data[4].trim() || '';
            return name === selectedBookedByName.trim();
        });

        this.destroyExtraTable();
        this.extraTable = $('.self-table').DataTable({
            dom: '<il<t>p>',
            paging: true,
            info: true,
            scrollX: true,
            order: [[0, 'asc']],
            language: {
                lengthMenu: this.$t('datatable.lengthMenu') as string,
                info: this.$t('datatable.info') as string,
                infoEmpty: this.$t('datatable.infoEmpty') as string,
                zeroRecords: this.$t('datatable.zeroRecords') as string,
                paginate: {
                    first: this.$t('datatable.first') as string,
                    last: this.$t('datatable.last') as string,
                    next: this.$t('datatable.next') as string,
                    previous: this.$t('datatable.previous') as string
                }
            },
            columnDefs: [
                {
                    type: 'formatted-num',
                    targets: 'sum'
                }
            ]
        });

        // Clear the previously appended elements
        const tabSelectors = ['#detailed', '#summary'];

        tabSelectors.forEach((tabSelector) => {
            const tab = document.querySelectorAll(tabSelector);
            tab.forEach((element) => {
                const entriesCount = element.querySelector('.self-table-info-entries-count');
                const lengthSelect = element.querySelector('.self-table-info-entries-select');

                if (entriesCount) {
                    entriesCount.innerHTML = '';
                }

                if (lengthSelect) {
                    lengthSelect.innerHTML = '';
                }
            });
        });

        const tables = document.querySelectorAll('.dataTables_wrapper');
        tables.forEach((table) => {
            const prev = table.previousElementSibling;
            const entriesCount = prev.querySelector('.self-table-info-entries-count');
            const lengthSelect = prev.querySelector('.self-table-info-entries-select');

            $(table).find('.dataTables_info').appendTo(entriesCount);
            $(table).find('.dataTables_length').appendTo(lengthSelect);
        });
        const selectTags = document.querySelectorAll('.self-table-header-select');
        selectTags.forEach((selectTag) => {
            if (selectTag) {
                selectTag.addEventListener('click', (event) => {
                    event.stopPropagation();
                });
            }
        });
        $('#passengerSelect').on('change', () => {
            this.extraTable.draw();
        });
        $('#bookedByNameSelect').on('change', () => {
            this.extraTable.draw();
        });
    }

    private destroyExtraTable() {
        if (this.extraTable != null) {
            this.extraTable.destroy();
            this.extraTable = null;
        }
    }

    async onLocaleChange() {
        this.SelectedToplevels.forEach((selectedItem) => {
            const matchingItem = this.toplevel.find((item) => item.toplevel_id === selectedItem.toplevel_id);

            if (matchingItem) {
                selectedItem.toplevel_name = matchingItem.toplevel_name;
            }
        });
        this.createExtraTable();
        this.initDescriptionExpansion();
    }

    // ============ Lifecycle methods ==========

    mounted() {
        eventBus.$on('dataFetched', this.onLocaleChange);
        this.clearAll();
        this.selectAllToplevels();
        this.initDescriptionExpansion();
    }

    beforeDestroy() {
        eventBus.$off('dataFetched', this.onLocaleChange);
    }

    updated(): void {
        if (this.serviceByDateResponse && this.requestSent && this.initExtraTable) {
            this.createExtraTable();
            this.initExtraTable = false;
            initTabs();
        }
    }

    destroyed(): void {
        $.fn.dataTable.ext.search.length = 0; // Clear the array of custom search functions
        this.clearAll();
    }
}
</script>

<style scoped></style>
